import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { mainAuth } from '../../auth/appAuth';
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from '../../auth/staffAuthApi';
import { fetchCurrentUser } from '../../api/me';

const WebsiteAdminAuthContext = createContext();

export const useWebsiteAdminAuth = () => {
  return useContext(WebsiteAdminAuthContext);
};

function parseSpecialAdmin(value) {
  if (value === true) return true;
  if (value === 'true') return true;
  return false;
}

export const WebsiteAdminAuthProvider = ({ children }) => {
  const [adminUser, setAdminUser] = useState(null);
  const [isSpecialAdmin, setIsSpecialAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const initialAuthResolvedRef = useRef(false);

  useEffect(() => {
    if (!mainAuth) {
      initialAuthResolvedRef.current = true;
      setLoading(false);
      return undefined;
    }

    const finishInitialLoad = () => {
      if (initialAuthResolvedRef.current) return;
      initialAuthResolvedRef.current = true;
      setLoading(false);
    };

    const refreshSpecialAdmin = async (user) => {
      try {
        const token = await user.getIdToken();
        const me = await fetchCurrentUser(token);
        setIsSpecialAdmin(parseSpecialAdmin(me?.user?.isSpecialAdmin ?? me?.isSpecialAdmin));
      } catch {
        setIsSpecialAdmin(false);
      }
    };

    const unsubscribe = onAuthStateChanged(mainAuth, (user) => {
      setAdminUser(user);
      if (!user) {
        setIsSpecialAdmin(false);
        finishInitialLoad();
        return;
      }

      void refreshSpecialAdmin(user).finally(finishInitialLoad);
    });

    return unsubscribe;
  }, []);

  const adminLogin = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(mainAuth, email, password);
      localStorage.setItem('websiteAuthUser', JSON.stringify(userCredential.user));
      return userCredential;
    } catch (error) {
      throw error;
    }
  };

  const adminLogout = async () => {
    try {
      await signOut(mainAuth);
      localStorage.removeItem('websiteAuthUser');
    } catch (error) {
      throw error;
    }
  };

  const value = {
    adminUser,
    isSpecialAdmin,
    adminLogin,
    adminLogout,
    loading
  };

  return (
    <WebsiteAdminAuthContext.Provider value={value}>
      {children}
    </WebsiteAdminAuthContext.Provider>
  );
}; 